import { useCallback, useEffect, useState } from "react";
import { Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { SbnLinkSearch } from "./SbnLinkSearch";
import { SbnSessionPicker } from "./SbnSessionPicker";
import { SbnResultsTable } from "./SbnResultsTable";
import { GripNormsPanel } from "./GripNormsPanel";
import { useGripNorms } from "./useGripNorms";
import { KneeNormsPanel } from "./KneeNormsPanel";

// Strength by Numbers results pulled into this assessment (handgrip etc.).
export function SbnResultsSection({ assessment, readOnly, onUpdated }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);
  const id = assessment?.id;
  const peak = assessment?.sbn_results?.grip_peak_kg;
  const norms = useGripNorms(id, peak?.left ?? null, peak?.right ?? null);

  const load = useCallback(() => {
    if (!id) return;
    setError(null);
    api.get(`/assessments/${id}/sbn-sessions`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function pull(sessionIds) {
    const r = await api.post(`/assessments/${id}/sbn-pull`, { session_ids: sessionIds });
    onUpdated(r.data);
    setPicking(false);
    const n = (r.data.sbn_results?.tests || []).length;
    const filled = r.data.sbn_filled_fields || [];
    const skipped = r.data.sbn_skipped_fields || [];
    const desc = [
      filled.length ? "Handgrip Peak Force filled into the grip fields." : "",
      skipped.length ? `Kept the grip value(s) you typed (${skipped.length}).` : "",
      !filled.length && !skipped.length ? "No handgrip result in these sessions." : "",
    ].filter(Boolean).join(" ");
    toast.success(`Pulled ${n} SbN test${n === 1 ? "" : "s"} into the assessment`, { description: desc });
  }

  async function remove() {
    if (!window.confirm("Remove the SbN results table from this assessment? Grip fields keep their values.")) return;
    const r = await api.delete(`/assessments/${id}/sbn-results`);
    onUpdated(r.data);
  }

  if (data && !data.configured) return null;
  const results = assessment?.sbn_results;
  const showPicker = !readOnly && data?.link && (picking || !results);

  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid="sbn-results-section">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-zinc-500" />
        <h2 className="font-display font-semibold text-zinc-950">Strength by Numbers results</h2>
        {data?.link && (
          <span className="text-2xs text-zinc-500" data-testid="sbn-results-linked-name">
            · SbN client {data.link.sbn_client_name || data.link.sbn_client_id}
          </span>
        )}
      </div>
      {error && <p className="text-sm text-red-700" data-testid="sbn-results-error">{error}</p>}
      {!data && !error && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
      {results && <SbnResultsTable results={results} readOnly={readOnly} onChange={() => setPicking(true)} onRemove={remove} />}
      {results && peak && <GripNormsPanel cmp={norms} testId="sbn-grip-norms" />}
      {results && (
        <KneeNormsPanel
          assessmentId={id}
          refreshKey={`${results.pulled_at}|${assessment.responses?.vitals_weight_kg?.value ?? ""}`}
        />
      )}
      {data && !data.link && !readOnly && (
        <SbnLinkSearch email={data.email} clientName={assessment.client_name} onLinked={load} />
      )}
      {showPicker && (
        <SbnSessionPicker
          sessions={data.sessions}
          suggestedIds={data.suggested_session_ids}
          onPull={pull}
          onCancel={results ? () => setPicking(false) : null}
        />
      )}
    </Card>
  );
}
